import Qs from 'qs'

let wechatScriptPromise = null
let wechatEntryUrl = ''

export function captureWechatEntryUrl () {
  if (!wechatEntryUrl) {
    wechatEntryUrl = window.location.href.split('#')[0]
  }
  return wechatEntryUrl
}

export function getWechatSigningUrl () {
  // iOS 微信校验首次进入文档的地址，必须在路由改写前保存。
  const ua = window.navigator.userAgent || ''
  const isIos = /iphone|ipad|ipod/i.test(ua) ||
    (/macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1)
  return isIos ? captureWechatEntryUrl() : window.location.href.split('#')[0]
}

function finishOnce (resolve, value, state) {
  if (state.done) return
  state.done = true
  resolve(value)
}

export function isWechatBrowser () {
  return /micromessenger/i.test(window.navigator.userAgent || '')
}

export function describeWechatReadyTimeout () {
  const embedded = window.self !== window.top
  const bridgeReady = !!(window.WeixinJSBridge && typeof window.WeixinJSBridge.invoke === 'function')
  const lines = [
    '微信 JS-SDK ready 超时（8秒内未收到微信校验回调）',
    '页面方式：' + (embedded ? 'iframe 内嵌页面' : '顶层页面'),
    '微信桥接：' + (bridgeReady ? '已存在' : '未就绪'),
    '当前页面域名：' + window.location.host
  ]
  if (!bridgeReady) {
    lines.push(embedded
      ? '当前内嵌页面未获得微信桥接，请对接方确认打开方式。'
      : '当前页面尚未获得微信桥接。')
  } else {
    lines.push('微信桥接已存在，但未返回配置校验结果。')
  }
  return lines.join('\n')
}

export function ensureWechatScript (timeoutMs) {
  const timeout = Number(timeoutMs) > 0 ? Number(timeoutMs) : 3000

  if (window.wx) {
    return Promise.resolve(window.wx)
  }

  if (wechatScriptPromise) {
    return wechatScriptPromise
  }

  wechatScriptPromise = new Promise((resolve, reject) => {
    let finished = false
    let timer = null

    const cleanup = () => {
      if (timer) {
        window.clearTimeout(timer)
        timer = null
      }
    }

    const done = () => {
      if (finished) return
      finished = true
      cleanup()
      if (window.wx) {
        resolve(window.wx)
      } else {
        reject(new Error('微信 JS-SDK 加载完成但 window.wx 不存在'))
      }
    }

    const fail = () => {
      if (finished) return
      finished = true
      cleanup()
      reject(new Error('微信 JS-SDK 加载失败'))
    }

    timer = window.setTimeout(() => {
      if (finished) return
      finished = true
      reject(new Error('微信 JS-SDK 加载超时'))
    }, timeout)

    const current = document.getElementById('wechat-jssdk')
    if (current) {
      current.addEventListener('load', done, { once: true })
      current.addEventListener('error', fail, { once: true })
      return
    }

    const script = document.createElement('script')
    script.id = 'wechat-jssdk'
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
    script.onload = done
    script.onerror = fail
    const parentNode = document.head || document.body || document.documentElement
    parentNode.appendChild(script)
  }).then(wx => {
    return wx
  }, error => {
    // 失败后允许后续重试，避免一次网络抖动后整个会话永久不可用。
    wechatScriptPromise = null
    throw error
  })

  return wechatScriptPromise
}

export async function detectWechatRuntime () {
  const ua = window.navigator.userAgent || ''

  if (!/micromessenger/i.test(ua)) {
    return 'browser'
  }

  // 部分微信客户端会直接在 UA 中包含 MiniProgram，可先快速判断。
  if (/miniprogram/i.test(ua)) {
    return 'miniProgram'
  }

  try {
    await ensureWechatScript(2500)
  } catch (error) {
    // 能确认是微信内置浏览器，但 SDK 加载失败时，默认按公众号 H5 处理。
    return 'wechat'
  }

  if (window.__wxjs_environment === 'miniprogram') {
    return 'miniProgram'
  }

  return new Promise(resolve => {
    const state = { done: false }
    const timer = window.setTimeout(() => {
      finishOnce(resolve, 'wechat', state)
    }, 1200)

    const finish = value => {
      if (state.done) return
      window.clearTimeout(timer)
      finishOnce(resolve, value, state)
    }

    if (!window.wx || !window.wx.miniProgram || typeof window.wx.miniProgram.getEnv !== 'function') {
      finish('wechat')
      return
    }

    try {
      window.wx.miniProgram.getEnv(res => {
        finish(res && res.miniprogram ? 'miniProgram' : 'wechat')
      })
    } catch (error) {
      finish('wechat')
    }
  })
}

export async function configWechatJssdk (http, options) {
  const opts = options || {}
  const wxId = opts.wxId || 10
  const apiList = Array.isArray(opts.jsApiList) && opts.jsApiList.length
    ? opts.jsApiList
    : ['scanQRCode', 'getLocation']

  if (!isWechatBrowser()) {
    return { ready: false, reason: 'not-wechat' }
  }

  await ensureWechatScript(3000)

  const signingUrl = getWechatSigningUrl()
  const res = await http.post('/wxapi/getJsapiConfigInfo', Qs.stringify({
    wxId: wxId,
    url: signingUrl
  }), {
    timeout: 10000
  })

  if (!res || res.result !== '1' || !res.data || !window.wx) {
    return {
      ready: false,
      reason: (res && (res.desc || res.data)) || '微信 JS-SDK 配置获取失败'
    }
  }

  return new Promise(resolve => {
    let finished = false
    let timer = null

    const finish = result => {
      if (finished) return
      finished = true
      if (!result.ready) {
        // 仅记录地址路径，避免把入口 ticket 等查询参数输出到日志。
        console.warn('[wpc-h5] wechat config rejected', {
          reason: result.reason,
          appId: res.data.app_id,
          signingPage: signingUrl.split('?')[0],
          currentPage: window.location.href.split('#')[0].split('?')[0],
          usesEntryUrl: signingUrl === wechatEntryUrl
        })
      }
      if (timer) {
        window.clearTimeout(timer)
        timer = null
      }
      resolve(result)
    }

    timer = window.setTimeout(() => {
      finish({ ready: false, reason: describeWechatReadyTimeout() })
    }, 8000)

    try {
      window.wx.ready(() => {
        finish({ ready: true, reason: '' })
      })

      if (typeof window.wx.error === 'function') {
        window.wx.error(error => {
          finish({
            ready: false,
            reason: error && error.errMsg ? error.errMsg : '微信 JS-SDK 配置失败'
          })
        })
      }

      window.wx.config({
        debug: false,
        appId: res.data.app_id,
        timestamp: parseInt(res.data.time_stamp, 10),
        nonceStr: res.data.nonce_str,
        signature: res.data.signature,
        jsApiList: apiList
      })
    } catch (error) {
      finish({ ready: false, reason: error.message || '微信 JS-SDK 配置异常' })
    }
  })
}



// WEBPACK FOOTER //
// ./src/utils/wechat.js